import { useId, forwardRef, useRef, useImperativeHandle } from 'react';
import styles from './ui-form-textarea.module.scss';
import { Link } from 'react-router';
import { UIText } from '../ui-text';
import { UITextButton } from '../..';

export type UIFormTextareaProps = {
    name?: string;
    label?: string;
    labelButton?: {
        text: string;
        link?: string;
        onClick?: () => void;
    };
    value?: string;
    placeholder?: string;
    description?: string;
    required?: boolean;
    disabled?: boolean;
    autoComplete?: 'on' | 'off';
    maxLength?: number;
    rows?: number;
    cols?: number;
    resize?: 'none' | 'both' | 'horizontal' | 'vertical';
    errorText?: string;
    autoFocus?: boolean;
    onChange?: (value: string) => void;
    onFocusChange?: (isFocused: boolean) => void;
    onShortcutSubmit?: () => void;
    height?: number | string;
    width?: number | string;
    maxHeight?: number | string;
    maxWidth?: number | string;
};

export type UIFormTextareaRef = {
    focus: () => void;
};

export const UIFormTextarea = forwardRef<
    UIFormTextareaRef,
    UIFormTextareaProps
>((props, ref) => {
    const {
        name,
        label,
        labelButton,
        value,
        placeholder,
        description = '',
        required = false,
        disabled = false,
        autoComplete,
        maxLength,
        rows = 4,
        cols,
        resize = 'vertical',
        errorText = '',
        autoFocus = false,
        onChange,
        onFocusChange,
        onShortcutSubmit,
        height,
        width,
        maxHeight,
        maxWidth
    } = props;

    const id = useId();

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => ({
        focus() {
            textareaRef.current?.focus();
        }
    }));

    const textareaClasses = [styles.textarea];
    if (errorText) {
        textareaClasses.push(styles.textareaError);
    }
    if (disabled) {
        textareaClasses.push(styles.textareaDisabled);
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            onShortcutSubmit?.();
        }
    };

    return (
        <div className={styles.container}>
            {label && (
                <div className={styles.labelContainer}>
                    <div>
                        <label className={styles.label} htmlFor={id}>
                            {label}
                        </label>
                        {required ? (
                            <label
                                className={`${styles.label} ${styles.labelRequired}`}
                                htmlFor={id}
                            >
                                {' *'}
                            </label>
                        ) : undefined}
                    </div>

                    {labelButton && labelButton.link && (
                        <Link
                            to={labelButton.link}
                            style={{ textDecoration: 'none' }}
                        >
                            <UITextButton variant="primary" type="button">
                                {labelButton.text}
                            </UITextButton>
                        </Link>
                    )}

                    {labelButton && labelButton.onClick && (
                        <UITextButton
                            onClick={labelButton.onClick}
                            variant="primary"
                            type="button"
                        >
                            {labelButton.text}
                        </UITextButton>
                    )}
                </div>
            )}
            <textarea
                ref={textareaRef}
                className={textareaClasses.join(' ')}
                style={{
                    height,
                    width,
                    maxHeight,
                    maxWidth,
                    resize
                }}
                id={id}
                value={value}
                name={name}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                autoComplete={autoComplete}
                maxLength={maxLength}
                rows={rows}
                cols={cols}
                aria-invalid={!!errorText}
                onChange={(e) => {
                    onChange?.(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                autoFocus={autoFocus}
                onFocus={() => onFocusChange?.(true)}
                onBlur={() => onFocusChange?.(false)}
            />
            {errorText && (
                <div className={styles.errorTextContainer}>
                    <UIText variant="small" type="error">
                        {errorText}
                    </UIText>
                </div>
            )}
            {!errorText && description && (
                <div className={styles.descriptionTextContainer}>
                    <UIText variant="small" type="default2">
                        {description}
                    </UIText>
                </div>
            )}
        </div>
    );
});
